// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { CommonModule } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  input,
  computed,
  signal,
  effect,
  HostBinding,
} from '@angular/core';

/**
 * Avatar type variants matching Figma Coherence UI Kit
 * - photo: Circular user image (fallback: user icon placeholder)
 * - initials: Circular with 1-2 letter initials from name
 * - organization: Rounded rectangle with logo (fallback: building icon placeholder)
 * - project: Rounded rectangle with logo (fallback: laptop-code icon placeholder)
 */
export type AvatarType = 'photo' | 'initials' | 'organization' | 'project';

/**
 * Avatar size variants matching Figma design tokens
 * xs: 16px, sm: 24px, md: 32px, lg: 48px, xl: 64px, xxl: 120px
 */
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

@Component({
  selector: 'lfx-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarComponent {
  /**
   * Avatar type: 'photo' | 'initials' | 'organization' | 'project'
   * @default 'photo'
   */
  readonly type = input<AvatarType>('photo');

  /**
   * Avatar size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
   * @default 'md' (32px)
   */
  readonly size = input<AvatarSize>('md');

  /**
   * Host class bindings for size variants
   */
  @HostBinding('class.avatar-host--xs')
  get isXsSize(): boolean {
    return this.size() === 'xs';
  }

  @HostBinding('class.avatar-host--sm')
  get isSmSize(): boolean {
    return this.size() === 'sm';
  }

  @HostBinding('class.avatar-host--md')
  get isMdSize(): boolean {
    return this.size() === 'md';
  }

  @HostBinding('class.avatar-host--lg')
  get isLgSize(): boolean {
    return this.size() === 'lg';
  }

  @HostBinding('class.avatar-host--xl')
  get isXlSize(): boolean {
    return this.size() === 'xl';
  }

  @HostBinding('class.avatar-host--xxl')
  get isXxlSize(): boolean {
    return this.size() === 'xxl';
  }

  /**
   * Image URL for photo or organization logo
   * When null/empty, shows appropriate placeholder
   */
  readonly imageUrl = input<string | null>(null);

  /**
   * Name used for generating initials
   * For 'xs' size, only first initial is shown
   */
  readonly name = input<string>('');

  /**
   * Legacy support: bordered style (maps to organization type internally)
   * @deprecated Use type='organization' instead
   */
  readonly bordered = input<boolean>(false);

  /**
   * Legacy support: imageURL (maps to imageUrl)
   * @deprecated Use imageUrl instead
   */
  readonly imageURL = input<string>('');

  /**
   * Legacy support: object-fit cover vs contain
   * When true (default), uses object-fit: cover
   * When false, uses object-fit: contain (useful for logos)
   */
  readonly isObjectCover = input<boolean>(true);

  /**
   * Legacy support: use CDN letter avatar when no image provided
   * When true and no imageUrl, generates URL like https://lfx-cdn-prod.s3.amazonaws.com/users/avatar/a.png
   * @default true for backward compatibility
   */
  readonly useLegacyCdnFallback = input<boolean>(true);

  /** Internal state for tracking primary image load errors */
  protected readonly primaryImageError = signal<boolean>(false);

  /** Internal state for tracking fallback image load errors */
  protected readonly fallbackImageError = signal<boolean>(false);

  /** Effect to reset error states when URL inputs change (mirrors original ngOnChanges behavior) */
  private readonly resetErrorOnUrlChange = effect(() => {
    // Track URL inputs - when they change, reset error states to give new URL a chance to load
    const url = this.imageUrl();
    const legacyUrl = this.imageURL();

    // Only reset if there's actually a URL to try
    if (url || legacyUrl) {
      this.primaryImageError.set(false);
      this.fallbackImageError.set(false);
    }
  }, { allowSignalWrites: true });

  /** Computed: Legacy CDN avatar URL based on first letter of name */
  protected readonly legacyCdnAvatarUrl = computed(() => {
    const name = this.name();
    if (!name) return null;

    const cleanName = name.replace(/[^a-zA-Z]/g, '');
    if (!cleanName) return null;

    const firstChar = cleanName[0].toLowerCase();
    return `https://lfx-cdn-prod.s3.amazonaws.com/users/avatar/${firstChar}.png`;
  });

  /** Computed: Primary image URL from inputs */
  protected readonly primaryImageUrl = computed(() => {
    return this.imageUrl() || this.imageURL() || null;
  });

  /** Computed: Whether to use fallback (primary failed, fallback available) */
  protected readonly shouldUseFallback = computed(() => {
    const hasPrimaryUrl = !!this.primaryImageUrl();
    const primaryFailed = this.primaryImageError();
    const fallbackAvailable = this.type() === 'photo' && this.useLegacyCdnFallback() && !!this.legacyCdnAvatarUrl();
    return hasPrimaryUrl && primaryFailed && fallbackAvailable;
  });

  /** Computed: Effective image URL (supports legacy imageURL input and CDN fallback) */
  protected readonly effectiveImageUrl = computed(() => {
    const primaryUrl = this.primaryImageUrl();

    // If primary URL exists and hasn't errored, use it
    if (primaryUrl && !this.primaryImageError()) {
      return primaryUrl;
    }

    // If primary errored, try CDN fallback for photo type
    if (this.primaryImageError() && this.type() === 'photo' && this.useLegacyCdnFallback()) {
      return this.legacyCdnAvatarUrl();
    }

    // No primary URL provided - use CDN fallback if enabled
    if (!primaryUrl && this.type() === 'photo' && this.useLegacyCdnFallback()) {
      return this.legacyCdnAvatarUrl();
    }

    return primaryUrl;
  });

  /** Computed: Whether type uses rounded rectangle shape (organization, project) */
  protected readonly isRectangularType = computed(() => {
    const type = this.type();
    return type === 'organization' || type === 'project' || this.bordered();
  });

  /** Computed: Effective type considering legacy bordered support */
  protected readonly effectiveType = computed(() => {
    if (this.bordered() && this.type() === 'photo') {
      return 'organization';
    }
    return this.type();
  });

  // =============================================================================
  // TAILWIND CLASS COMPUTATIONS
  // =============================================================================

  /** Size to Tailwind dimension classes mapping */
  private readonly sizeToTwDimensions: Record<AvatarSize, string> = {
    xs: 'tw-w-4 tw-h-4',           // 16px
    sm: 'tw-w-6 tw-h-6',           // 24px
    md: 'tw-w-8 tw-h-8',           // 32px
    lg: 'tw-w-12 tw-h-12',         // 48px
    xl: 'tw-w-16 tw-h-16',         // 64px
    xxl: 'tw-w-[120px] tw-h-[120px]', // 120px
  };

  /** Size to border-radius for rectangular types (organization/project) */
  private readonly sizeToRectRadius: Record<AvatarSize, string> = {
    xs: 'tw-rounded-sm',           // 2px
    sm: 'tw-rounded',              // 4px
    md: 'tw-rounded-md',           // 6px
    lg: 'tw-rounded-md',           // 6px
    xl: 'tw-rounded-lg',           // 8px
    xxl: 'tw-rounded-xl',          // 12px
  };

  /** Size to initials font size classes */
  private readonly sizeToInitialsFontSize: Record<AvatarSize, string> = {
    xs: 'tw-text-[10px] tw-leading-[14px]',
    sm: 'tw-text-xs tw-leading-4',
    md: 'tw-text-base tw-leading-6',
    lg: 'tw-text-2xl tw-leading-9',
    xl: 'tw-text-[30px] tw-leading-[44px]',
    xxl: 'tw-text-5xl tw-leading-[72px]',
  };

  /** Computed: Avatar container Tailwind classes */
  protected readonly avatarClasses = computed(() => {
    const size = this.size();
    const effectiveType = this.effectiveType();
    const hasImage = this.showImage();

    // Base classes
    const baseClasses = 'tw-inline-flex tw-items-center tw-justify-center tw-overflow-hidden tw-flex-shrink-0';

    // Size classes
    const sizeClasses = this.sizeToTwDimensions[size];

    // Background color (neutral-100 for placeholders/initials, white for images on org/project)
    const isOrgOrProject = effectiveType === 'organization' || effectiveType === 'project';
    const bgClass = hasImage && isOrgOrProject ? 'tw-bg-white' : 'tw-bg-lf-neutral-100';

    // Border radius based on type
    let radiusClass: string;
    if (effectiveType === 'photo' || effectiveType === 'initials') {
      radiusClass = 'tw-rounded-full';
    } else {
      radiusClass = this.sizeToRectRadius[size];
    }

    // Border for organization/project with image
    const borderClass = hasImage && isOrgOrProject ? 'tw-border tw-border-solid tw-border-lf-neutral-200' : '';

    return `${baseClasses} ${sizeClasses} ${bgClass} ${radiusClass} ${borderClass}`.trim().replace(/\s+/g, ' ');
  });

  /** Computed: Initials Tailwind classes */
  protected readonly initialsClasses = computed(() => {
    const size = this.size();
    const fontSizeClasses = this.sizeToInitialsFontSize[size];
    return `tw-font-lf-inter tw-font-medium tw-text-lf-neutral-500 tw-text-center tw-select-none ${fontSizeClasses}`;
  });

  /** Computed: Image Tailwind classes */
  protected readonly imageTwClasses = computed(() => {
    const objectFitClass = this.isObjectCover() ? 'tw-object-cover' : 'tw-object-contain';
    return `tw-w-full tw-h-full ${objectFitClass}`;
  });

  /** Computed: Placeholder Tailwind classes */
  protected readonly placeholderClasses = computed(() => {
    return 'tw-w-1/2 tw-h-1/2 tw-object-contain';
  });

  /** Computed: Whether all image options have failed */
  protected readonly allImagesFailed = computed(() => {
    const hasPrimaryUrl = !!this.primaryImageUrl();
    const primaryFailed = this.primaryImageError();
    const fallbackFailed = this.fallbackImageError();
    const hasFallback = this.type() === 'photo' && this.useLegacyCdnFallback() && !!this.legacyCdnAvatarUrl();

    // If no primary URL, check if fallback is available and failed
    if (!hasPrimaryUrl) {
      return !hasFallback || fallbackFailed;
    }

    // If primary failed, check if fallback exists and also failed
    if (primaryFailed) {
      return !hasFallback || fallbackFailed;
    }

    return false;
  });

  /** Computed: Whether to show the image */
  protected readonly showImage = computed(() => {
    const url = this.effectiveImageUrl();
    const hasValidUrl = url !== null && url !== '';
    const type = this.type();
    return (type === 'photo' || type === 'organization' || type === 'project') && hasValidUrl && !this.allImagesFailed();
  });

  /** Computed: Whether to show initials */
  protected readonly showInitials = computed(() => {
    const type = this.type();

    // Explicit initials type
    if (type === 'initials') return true;

    // For photo type with failed image in non-legacy mode, fall back to initials if name exists
    if (type === 'photo' && this.allImagesFailed() && !this.useLegacyCdnFallback()) {
      const name = this.name();
      return !!name && name.trim().length > 0;
    }

    return false;
  });

  /** Computed: Whether to show placeholder icon */
  protected readonly showPlaceholder = computed(() => {
    const type = this.type();

    // Never show placeholder for explicit initials type
    if (type === 'initials') return false;

    // If showing image, don't show placeholder
    if (this.showImage()) return false;

    // If showing initials (fallback), don't show placeholder
    if (this.showInitials()) return false;

    // Show placeholder when no image URL or all images failed
    const url = this.effectiveImageUrl();
    return !url || url === '' || this.allImagesFailed();
  });

  /** Computed: Placeholder SVG path based on type */
  protected readonly placeholderSvg = computed(() => {
    const effectiveType = this.bordered() ? 'organization' : this.type();
    switch (effectiveType) {
      case 'organization':
        return 'assets/img/avatars/avatar-organization-placeholder.svg';
      case 'project':
        return 'assets/img/avatars/avatar-project-placeholder.svg';
      default:
        return 'assets/img/avatars/avatar-photo-placeholder.svg';
    }
  });


  /** Computed: Initials to display */
  protected readonly initials = computed(() => {
    const name = this.name();
    if (!name) return 'A';

    const cleanName = name.replace(/[^a-zA-Z\s]/g, '').trim();
    if (!cleanName) return 'A';

    const words = cleanName.split(/\s+/).filter(w => w.length > 0);

    // For xs size, only show single initial
    if (this.size() === 'xs') {
      return words[0]?.charAt(0).toUpperCase() || 'A';
    }

    // For other sizes, show up to 2 initials
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }

    return words.slice(0, 2)
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  });

  /** Handle image load error - fall back to CDN letter avatar or placeholder */
  protected onImageError(): void {
    // If primary hasn't errored yet, this is the primary failing
    if (!this.primaryImageError()) {
      this.primaryImageError.set(true);
    } else {
      // Primary already failed, this must be the fallback failing
      this.fallbackImageError.set(true);
    }
  }

  /** Reset error states on successful image load */
  protected onImageLoad(): void {
    // Image loaded successfully - no need to change error states
    // The error states are reset by the effect when URL inputs change
  }
}
